import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GlobeAltIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';
import { useAuth } from '../../../contexts/AuthContext';
import { subdomainAPI } from '../../../services/api';
import { getSubdomain, isSubdomainAccess } from '../../../services/subdomain';

export interface CompanyInfo {
  id: number;
  company_name: string;
  username: string;
  contact_person: string;
  city: string;
  logo_url: string | null;
  has_custom_branding?: boolean;
}

export const useCustomerLogin = () => {
  const [loginForm, setLoginForm] = useState({
    username: '', password: '', showPassword: false,
  });
  const { username, password, showPassword } = loginForm;
  const [formStatus, setFormStatus] = useState({ error: '', loading: false });
  const { error, loading } = formStatus;
  const [companyState, setCompanyState] = useState<{ info: CompanyInfo | null; error: string }>({ info: null, error: '' });
  const { info: companyInfo, error: subdomainError } = companyState;
  const [mfaState, setMfaState] = useState<{
    pending: { mfa_token: string } | null; code: string;
  }>({ pending: null, code: '' });
  const { pending: mfaPending, code: mfaCode } = mfaState;
  const { login, completeMfaLogin } = useAuth();
  const navigate = useNavigate();
  const subdomain = getSubdomain();
  const isCompanyPortal = isSubdomainAccess();

  useEffect(() => {
    if (isCompanyPortal) {
      subdomainAPI.getCompanyInfo()
        .then(res => setCompanyState(prev => ({ ...prev, info: res.data })))
        .catch(() => setCompanyState(prev => ({ ...prev, error: 'not_found' })));
    }
  }, [isCompanyPortal]);

  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  const _navigateAfterLogin = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      if (parsed.role === 'company') { navigate('/company/dashboard'); return; }
      if (parsed.role === 'admin') { navigate('/manage/settings'); return; }
    }
    navigate('/customer-dashboard');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus({ error: '', loading: true });
    setUnverifiedEmail('');
    setResendStatus('idle');

    try {
      const result = await login(username, password, false);
      if (result === true) {
        _navigateAfterLogin();
      } else if (typeof result === 'object' && result.mfa_required) {
        setMfaState(prev => ({ ...prev, pending: { mfa_token: result.mfa_token } }));
      } else {
        setFormStatus(prev => ({ ...prev, error: 'Invalid credentials. Please check your username and password.' }));
      }
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.email_unverified) {
        setUnverifiedEmail(data.email || '');
        setFormStatus(prev => ({ ...prev, error: data.error || 'Please verify your email address before logging in.' }));
      } else if (err?.response?.status === 403) {
        setFormStatus(prev => ({ ...prev, error: data?.error || 'Access denied.' }));
      } else {
        setFormStatus(prev => ({ ...prev, error: 'An error occurred during login. Please try again.' }));
      }
    } finally {
      setFormStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://127.0.0.1:8001/api'
        : '/api';
      await fetch(`${apiBase}/auth/resend-verification/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: unverifiedEmail }),
      });
      setResendStatus('sent');
    } catch {
      setResendStatus('idle');
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaPending) return;
    setFormStatus({ error: '', loading: true });

    try {
      const result = await completeMfaLogin(mfaPending.mfa_token, mfaCode);
      if (result) {
        _navigateAfterLogin();
      } else {
        setFormStatus(prev => ({ ...prev, error: 'Invalid MFA code. Please try again.' }));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'MFA verification failed.';
      setFormStatus(prev => ({ ...prev, error: msg }));
    } finally {
      setFormStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleBackToLogin = () => {
    setMfaState({ pending: null, code: '' });
    setFormStatus(prev => ({ ...prev, error: '' }));
  };

  const benefits = [
    { icon: GlobeAltIcon, title: 'Branded Portal', desc: 'Your own subdomain at yourbakery.sourdoughsy.com' },
    { icon: UserGroupIcon, title: 'Customer Management', desc: 'Manage customers, orders, and accounts in one place' },
    { icon: ChartBarIcon, title: 'Financial Reports', desc: 'Track revenue, balances, and growth with live charts' },
    { icon: ShieldCheckIcon, title: 'Secure & Reliable', desc: 'Enterprise-grade security for your bakery data' },
  ];

  const lblStyle: React.CSSProperties = { fontWeight: 600, fontSize: '0.88rem', color: '#374151' };
  const inputStyle: React.CSSProperties = {
    padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #e2e8f0', fontSize: '0.92rem',
    transition: 'border-color 0.2s',
  };

  return {
    username,
    password,
    showPassword,
    error,
    loading,
    companyInfo,
    subdomainError,
    mfaPending,
    mfaCode,
    subdomain,
    isCompanyPortal,
    setLoginForm,
    setMfaState,
    navigate,
    handleSubmit,
    handleMfaSubmit,
    handleBackToLogin,
    handleResendVerification,
    unverifiedEmail,
    resendStatus,
    benefits,
    lblStyle,
    inputStyle,
  };
};
